import { analytics } from "../analytics";
import { handleError } from "@/config/errorHandling";

interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  timeout: number;
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 1,
  retryDelay: 1000,
  timeout: 10000,
};

/**
 * Track Successful Intercept.
 */
function trackSuccessfulIntercept<T>(
  result: T,
  context: string,
  startTime: number,
  trackPerformance: boolean
): Promise<T> {
  const duration = Date.now() - startTime;

  try {
    analytics.trackAPICall(context, "GET", 200, duration);
  } catch (error) {
    console.warn("Failed to track API call (non-critical):", error);
  }

  if (trackPerformance) {
    try {
      analytics.trackPerformance("api_response_time", duration, { context });
    } catch (error) {
      console.warn("Failed to track performance (non-critical):", error);
    }
  }

  return result;
}

/**
 * Handle Intercept Failure.
 */
async function handleInterceptFailure(
  error: unknown,
  context: string,
  startTime: number,
  attempt: number,
  maxRetries: number
): Promise<void> {
  const duration = Date.now() - startTime;

  try {
    console.warn("API call failed:", {
      context,
      method: "GET",
      status: 500,
      duration,
      error: (error as Error).message,
    });
  } catch (analyticsError) {
    console.warn("Failed to track failed API call (non-critical):", analyticsError);
  }

  try {
    analytics.handleError(error as Error, {
      context: "api_interceptor",
      apiContext: context,
      attempt,
      maxRetries,
      duration,
    });
  } catch (analyticsError) {
    console.warn("Failed to handle API error (non-critical):", analyticsError);
  }

  await handleError(error, `api_interceptor_${context}`, {
    enableDatabaseLogging: false,
    enableConsoleFallback: true,
  });
}

class APIInterceptor {
  private readonly retryConfig: RetryConfig;

  constructor(config: Partial<RetryConfig> = {}) {
    this.retryConfig = { ...defaultRetryConfig, ...config };
  }

  /**
   * Intercepts an API call, implementing retry logic and performance tracking.
   * @example
   * intercept(() => fetchDataFromApi(), 'fetchDataContext', { retryConfig: { maxRetries: 3 }, trackPerformance: true });
   * // Returns the fetched data or throws an error after final retry
   * @template T
   * @param {() => Promise<T>} apiCall - The API call that returns a promise.
   * @param {string} context - Descriptive context for the API call, used in logging and analytics.
   * @param {Object} [options] - Additional options to configure retry behavior and tracking.
   * @param {Partial<RetryConfig>} [options.retryConfig] - Optional retry configuration settings, such as maxRetries and retryDelay.
   * @param {boolean} [options.trackPerformance] - Optional flag to enable performance tracking of the API call.
   * @returns {Promise<T>} Resolves with the result of the API call if successful, or rejects with the last error after all retry attempts.
   */
  async intercept<T>(
    apiCall: () => Promise<T>,
    context: string,
    options: {
      retryConfig?: Partial<RetryConfig>;
      trackPerformance?: boolean;
    } = {}
  ): Promise<T> {
    const config = { ...this.retryConfig, ...options.retryConfig };
    const startTime = Date.now();
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= config.maxRetries; attempt += 1) {
      try {
        // Create a timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error("Request timeout")), config.timeout);
        });

        // Race between the API call and timeout
        const result = await Promise.race([apiCall(), timeoutPromise]);
        return trackSuccessfulIntercept(
          result,
          context,
          startTime,
          Boolean(options.trackPerformance)
        );
      } catch (error) {
        lastError = error as Error;
        await handleInterceptFailure(error, context, startTime, attempt, config.maxRetries);

        if (attempt === config.maxRetries) {
          throw error;
        }

        const delay = config.retryDelay * 2 ** (attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  }

  // Specific interceptor for Supabase calls
  /**
   * Intercepts a Supabase API call with custom retry and performance tracking settings.
   * @example
   * interceptSupabaseCall(asyncApiCallFunction, 'fetchUsers')
   * // Returns a promise with the intercepted API call.
   * @template T
   * @param {() => Promise<T>} apiCall - A function representing the API call to be intercepted.
   * @param {string} endpoint - The endpoint identifier used in logging and tracking performance.
   * @returns {Promise<T>} A promise that resolves with the result of the intercepted API call.
   */
  interceptSupabaseCall<T>(apiCall: () => Promise<T>, endpoint: string): Promise<T> {
    return this.intercept(apiCall, `supabase_${endpoint}`, {
      retryConfig: {
        maxRetries: 2, // Fewer retries for Supabase
        retryDelay: 500,
        timeout: 8000,
      },
      trackPerformance: true,
    });
  }

  // Interceptor for system logs and API request logs - with error handling
  /**
   * Intercept and manage logging API calls with specified configurations.
   *
   * @example
   * interceptLoggingCall(apiCallFunction, 'system_logs')
   *
   * @param {Function} apiCall - A function representing the API call to be executed.
   * @param {'system_logs'|'api_request_logs'} logType - Type of logging to be used for the API call.
   * @returns {Promise<T>} A promise that resolves to the result of the API call, or handles errors gracefully.
   */
  async interceptLoggingCall<T>(
    apiCall: () => Promise<T>,
    logType: "system_logs" | "api_request_logs"
  ): Promise<T> {
    try {
      return await this.intercept(apiCall, `logging_${logType}`, {
        retryConfig: {
          maxRetries: 1, // Don't retry logging calls
          retryDelay: 0,
          timeout: 5000,
        },
        trackPerformance: false,
      });
    } catch (error) {
      // Handle logging call errors gracefully - don't break the app
      await handleError(error, `logging_call_${logType}`, {
        enableDatabaseLogging: false, // Prevent infinite loops
        enableConsoleFallback: true,
      });

      // Return a default value or re-throw based on your needs
      throw error;
    }
  }
}

export const apiInterceptor = new APIInterceptor();
